import { Handbook } from '../../model/Handbook';
import styled from 'styled-components';
import handbooksData from '../../../assets/handbooks.json';
import { Colors } from '../../constants/colors';
import { Device } from '../../constants/device';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isPrerendering } from '../../helpers/isPrerendering';

const { handbooks } = handbooksData as unknown as { handbooks: Handbook[] };
const showcases = handbooks.filter((handbook) => handbook.showcase);
const SHOWCASE_ROOT = '/handbooks/showcase/';

/**
 * The customisable public laboratory handbook, or the hospital intranet built
 * the same way, shown on our customers' real handbooks: a large screenshot and thumbnails of the others to switch between,
 * so the variety of designs speaks for itself. Each visit starts on a random
 * one; the prerendered page starts on the first, so hydration matches, and the
 * pick happens after. Keeps the `handbooks` anchor.
 */
export const HandbookShowcase = (props: { kind: 'handbook' | 'intranet' }): ReactElement => {
  const { t } = useTranslation();
  const [featured, setFeatured] = useState<Handbook>(showcases[0]);
  const texts = props.kind === 'intranet' ? 'handbooks.intranet' : 'handbooks';
  const points = t(`${texts}.points`, { returnObjects: true }) as string[];

  useEffect(() => {
    if (!isPrerendering()) {
      setFeatured(showcases[Math.floor(Math.random() * showcases.length)]);
    }
  }, []);

  return (
    <Showcase id={'handbooks'}>
      <ShowcaseText>
        <h3>{t(`${texts}.title`)}</h3>
        <p>{t(`${texts}.text`)}</p>
        <Points>
          {points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </Points>
      </ShowcaseText>

      <Preview>
        <Screenshot href={featured.link} target="_blank" rel="noopener noreferrer">
          <BrowserBar>
            <i />
            <i />
            <i />
            <span>{new URL(featured.link).host}</span>
          </BrowserBar>
          <img src={SHOWCASE_ROOT + featured.showcase} alt={featured.title} width={1280} height={760} loading="lazy" />
        </Screenshot>
        <FeaturedName>{featured.title}</FeaturedName>
        <PickLabel>{t(`${texts}.pick`)}</PickLabel>
        <Thumbnails>
          {showcases.map((handbook) => (
            <li key={handbook.link}>
              <Thumbnail type="button" active={handbook.link === featured.link} aria-pressed={handbook.link === featured.link} title={handbook.title} onClick={() => setFeatured(handbook)}>
                <img src={`${SHOWCASE_ROOT}thumbs/${handbook.showcase}`} alt={handbook.title} width={320} height={190} loading="lazy" />
              </Thumbnail>
            </li>
          ))}
        </Thumbnails>
      </Preview>
    </Showcase>
  );
};

const Showcase = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 50px;
  align-items: center;
  width: 100%;
  margin-top: 60px;
  padding: 40px;
  border-radius: 20px;
  background: ${Colors.background};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  scroll-margin-top: 20px;

  ${Device.tablet} {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 25px;
  }
`;

const ShowcaseText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  h3 {
    margin: 0 0 12px;
    font-size: 28px;
    color: ${Colors.primaryDark};

    ${Device.tablet} {
      font-size: 22px;
    }
  }

  p {
    margin: 0;
    /* Overrides the centred, double spaced paragraph styling of Section. */
    text-align: left !important;
    line-height: 1.6 !important;
  }
`;

const Points = styled.ul`
  margin: 18px 0 0;
  padding-left: 22px;

  li {
    margin: 6px 0;
    line-height: 1.5;
    font-weight: 600;
  }
`;

const Preview = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Screenshot = styled.a`
  display: block;
  overflow: hidden;
  border-radius: 12px;
  background: ${Colors.white};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-3px);
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const BrowserBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${Colors.lighterGrey};

  i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${Colors.lightGrey};
  }

  span {
    margin-left: 10px;
    font-size: 12px;
    color: ${Colors.darkGrey};
  }
`;

const FeaturedName = styled.strong`
  margin-top: 14px;
  color: ${Colors.primaryDark};
`;

const PickLabel = styled.span`
  margin: 24px 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.darkGrey};
`;

const Thumbnails = styled.ul`
  display: grid;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${Device.tablet} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Thumbnail = styled.button<{ active: boolean }>`
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 2px solid ${({ active }) => (active ? Colors.primary : 'transparent')};
  border-radius: 6px;
  background: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  opacity: ${({ active }) => (active ? 1 : 0.75)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;
